import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'initializers/types';

import { getAlertsItems } from 'selectors/notifications';
import { getRequestsFeedExperiment } from 'selectors/session';

import { getRequestsFeed, markTabViewed } from './api';
import { REQUESTS_FEED_SLICE_NAME } from './constants';
import {
  NotificationsState,
  SelectedRequestState,
  UnreadCounts,
} from './types';

const initialState: NotificationsState = {
  selectedRequest: {
    request: null,
    type: null,
  },
  unreadCounts: {
    my: 0,
    team: 0,
    alerts: 0,
  },
  invalidatedTabs: {
    my: false,
    team: false,
  },
};

export const fetchUnreadCounts = createAsyncThunk(
  `${REQUESTS_FEED_SLICE_NAME}/fetchUnreadCounts`,
  async (locationId: string | undefined, { getState }) => {
    const response = await getRequestsFeed({
      tab: 'my',
      locationId,
    });

    const state = getState() as RootState;
    const alertsItems = state.get(getAlertsItems);
    const alertsCount = alertsItems ? alertsItems.size : 0;

    return {
      my: response.unreadCounts?.my || 0,
      team: response.unreadCounts?.team || 0,
      alerts: alertsCount,
    };
  }
);

export const markTabViewedAndFetchCounts = createAsyncThunk(
  `${REQUESTS_FEED_SLICE_NAME}/markTabViewed`,
  async (
    { tab, locationId }: { tab: 'my'; locationId?: string },
    { getState }
  ) => {
    const response = await markTabViewed({ tab, locationId });

    const state = getState() as RootState;
    const alertsItems = state.get(getAlertsItems);
    const alertsCount = alertsItems ? alertsItems.size : 0;

    return {
      my: response.unreadCounts?.my || 0,
      team: response.unreadCounts?.team || 0,
      alerts: alertsCount,
    };
  }
);

const slice = createSlice({
  name: REQUESTS_FEED_SLICE_NAME,
  initialState,
  reducers: {
    setSelectedRequest(state, action: PayloadAction<SelectedRequestState>) {
      state.selectedRequest.request = action.payload.request;
      state.selectedRequest.type = action.payload.type;
    },
    clearSelectedRequest(state) {
      state.selectedRequest.request = null;
      state.selectedRequest.type = null;
    },
    setUnreadCounts(state, action: PayloadAction<UnreadCounts>) {
      state.unreadCounts = action.payload;
    },
    invalidateTab(state, action: PayloadAction<'my' | 'team'>) {
      state.invalidatedTabs[action.payload] = true;
    },
    clearInvalidation(state, action: PayloadAction<'my' | 'team'>) {
      state.invalidatedTabs[action.payload] = false;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchUnreadCounts.fulfilled, (state, action) => {
        if (action.payload) {
          state.unreadCounts = action.payload;
        }
      })
      .addCase(markTabViewedAndFetchCounts.pending, state => {
        state.unreadCounts.my = 0;
      })
      .addCase(markTabViewedAndFetchCounts.fulfilled, (state, action) => {
        if (action.payload) {
          state.unreadCounts.my = action.payload.my;
          state.unreadCounts.team = action.payload.team;
          state.unreadCounts.alerts = action.payload.alerts;
        }
      });
  },
});

export const selectSelectedRequest = (state: RootState) =>
  state.get(REQUESTS_FEED_SLICE_NAME).selectedRequest;

export const selectUnreadCounts = (state: RootState) =>
  state.get(REQUESTS_FEED_SLICE_NAME).unreadCounts;

export const selectInvalidatedTabs = (state: RootState) =>
  state.get(REQUESTS_FEED_SLICE_NAME).invalidatedTabs;

export const selectHasUnreadNotifications = (state: RootState) => {
  const requestsFeedEnabled = getRequestsFeedExperiment(state);
  if (requestsFeedEnabled) {
    const unreadCounts = selectUnreadCounts(state);
    return (
      unreadCounts.my > 0 || unreadCounts.team > 0 || unreadCounts.alerts > 0
    );
  }
  return state.getIn(['nav', 'items', 'alerts', 'alertsExists'], false);
};

export const {
  setSelectedRequest,
  clearSelectedRequest,
  setUnreadCounts,
  invalidateTab,
  clearInvalidation,
} = slice.actions;

export const { reducer } = slice;
